<template>
  <c-input-select
    ref="searchSelectRef"
    :model-value="null"
    class="knowledge-base-search"
    :class="{ 'knowledge-base-search--large': large }"
    :options="options"
    option-label="title"
    use-input
    hide-dropdown-icon
    :input-debounce="300"
    :input-class="large ? 'body-large' : undefined"
    :loading="isSearching"
    :autofocus="autofocus"
    placeholder="Zoeken naar artikelen..."
    aria-label="Zoeken naar artikelen"
    @filter="onFilter"
    @update:model-value="onSelect"
  >
    <template #prepend>
      <q-icon
        :size="large ? '16px' : undefined"
        name="fa-regular fa-magnifying-glass"
        class="tw-text-gray-500"
      />
    </template>

    <template #option="{ itemProps, opt }">
      <c-select-option-header v-if="opt.kind === 'header'" :label="opt.label" />

      <c-select-option v-if="opt.kind !== 'header'" :item-props="itemProps">
        <c-highlight-text :text="opt.title" :needle="needle" />

        <c-menu-badge
          v-if="opt.kind === 'article' && !opt.is_published"
          class="tw-ml-2"
          label="concept"
          color="grey-4"
          text-color="dark"
        />

        <template v-if="opt.excerpt !== ''" #caption>
          <c-highlight-text :text="opt.excerpt" :needle="needle" />
        </template>
      </c-select-option>
    </template>
  </c-input-select>
</template>

<script setup lang="ts">
import { useFaqNavigation } from "composables/knowledge-base/useFaqNavigation";
import { stripHTMLTags } from "composables/useHelpers";
import type { KnowledgeBaseItem } from "models/knowledge-base/knowledge-base";
import type {
  FaqSearchMatch,
  KnowledgeBaseSearchEntry,
} from "models/knowledge-base/knowledge-base-search.types";
import { storeToRefs } from "pinia";
import { buildSearchExcerpt, foldSearchText } from "shared/utils";
import { useFaqStore } from "store/modules/faqStore";
import { useKnowledgeBaseStore } from "store/modules/knowledgeBaseStore";
import { onBeforeMount, ref, useTemplateRef } from "vue";
import { useRouter } from "vue-router";

interface Props {
  large?: boolean;
  autofocus?: boolean;
}

withDefaults(defineProps<Props>(), {
  large: false,
  autofocus: false,
});

const MIN_SEARCH_LENGTH = 2;
const MAX_FAQ_RESULTS = 5;

const router = useRouter();

const kbStore = useKnowledgeBaseStore();
const { isSearching, searchResults } = storeToRefs(kbStore);
const { searchItems, clearSearch } = kbStore;

const faqStore = useFaqStore();
const { categories: faqCategories } = storeToRefs(faqStore);
const { openCategory } = useFaqNavigation();

const searchSelectRef = useTemplateRef<{
  selectRef: {
    blur: () => void;
    updateInputValue: (value: string, noFilter?: boolean) => void;
  } | null;
}>("searchSelectRef");

const options = ref<KnowledgeBaseSearchEntry[]>([]);
const needle = ref("");

// Match against the plain text of the HTML content, so tag names and
// attributes (e.g. "div", "href") can never produce a hit.
const matchingFaqItems = (term: string): FaqSearchMatch[] => {
  const folded = foldSearchText(term);

  return faqCategories.value
    .flatMap((category) => category.items)
    .map((item) => ({ item, plainContent: stripHTMLTags(item.content) }))
    .filter(
      ({ item, plainContent }) =>
        foldSearchText(item.title).includes(folded) ||
        foldSearchText(plainContent).includes(folded),
    )
    .slice(0, MAX_FAQ_RESULTS);
};

const buildEntries = (
  articles: KnowledgeBaseItem[],
  faqMatches: FaqSearchMatch[],
  term: string,
): KnowledgeBaseSearchEntry[] => {
  const entries: KnowledgeBaseSearchEntry[] = [];

  if (articles.length > 0) {
    entries.push({
      kind: "header",
      uuid: "header-articles",
      label: "Artikelen",
      disable: true,
    });
    entries.push(
      ...articles.map((item) => ({
        kind: "article" as const,
        uuid: item.uuid,
        title: item.title,
        excerpt: buildSearchExcerpt(item.content, term),
        slug: item.slug,
        is_published: item.is_published,
      })),
    );
  }

  if (faqMatches.length > 0) {
    entries.push({
      kind: "header",
      uuid: "header-faq",
      label: "Veelgestelde vragen",
      disable: true,
    });
    entries.push(
      ...faqMatches.map(({ item, plainContent }) => ({
        kind: "faq" as const,
        uuid: item.uuid,
        title: item.title,
        excerpt: buildSearchExcerpt(plainContent, term),
        categoryUuid: item.category_uuid,
      })),
    );
  }

  return entries;
};

const onFilter = async (
  inputValue: string,
  update: (callback: () => void) => void,
  abort: () => void,
): Promise<void> => {
  const term = inputValue.trim();

  if (term.length < MIN_SEARCH_LENGTH) {
    clearSearch();
    options.value = [];
    needle.value = "";
    abort();

    return;
  }

  // The store guards races via its own search version; QSelect additionally
  // ignores update callbacks from a superseded filter run.
  await searchItems(term);

  update(() => {
    needle.value = term;
    options.value = buildEntries(
      searchResults.value,
      matchingFaqItems(term),
      term,
    );
  });
};

const onSelect = (entry: KnowledgeBaseSearchEntry | null): void => {
  if (entry === null || entry.kind === "header") {
    return;
  }

  if (entry.kind === "article") {
    clearSearch();
    router.push({ name: "knowledgeBaseItem", params: { slug: entry.slug } });
  }

  if (entry.kind === "faq") {
    // Opens the FAQ category on the index page with this question expanded.
    void openCategory(entry.categoryUuid, entry.uuid);
  }

  const select = searchSelectRef.value?.selectRef;
  select?.updateInputValue("", true);
  select?.blur();
  options.value = [];
  needle.value = "";
};

onBeforeMount(async () => {
  // FAQ results are matched client-side; make sure the data is there even when
  // this component mounts before (or without) the FAQ nav. The store dedupes
  // concurrent fetches.
  if (faqCategories.value.length === 0) {
    await faqStore.fetchAll();
  }
});
</script>

<style scoped lang="scss">
.knowledge-base-search--large {
  :deep(.q-field__control),
  :deep(.q-field__marginal) {
    min-height: 48px;
  }
}
</style>
